/**
 * The turn pipeline (HIG-5970/HIG-5971, design §3): decode → seed → fold →
 * apply patches through the MessageSink. One `for await` loop over the SSE
 * frames shared by submit and resume; failures land on the neverthrow Err
 * channel, interruption is a `TurnCompletion` — never an error.
 */
import { Result, ResultAsync, err, ok } from 'neverthrow'
import { applyEvent, ensureSeeded, initialTurnState, type MessageSeed, type TurnState } from './accumulator'
import {
  backendTurnError,
  interruptReasonFrom,
  sseConnectionError,
  toChatTurnError,
  type ChatTurnError,
  type TurnCompletion,
} from './errors'
import { decodeTurnEvent, isSubagentScoped, type ChatSSEEvent } from './events'
import { streamTurnFrames } from './sse'
import type { MessageSink, Telemetry } from './services'
import { describeCause, type StreamEndReason, type TurnScope } from './turn-trace'

export type ResumeCompletion = TurnCompletion<TurnState>

const INTERRUPTED_RESPONSE_MESSAGE = 'This response was interrupted. Please try again.'

export type TurnCheckpoint = {
  state: TurnState
  lastEventId?: number
  terminal: boolean
}

export type TurnPipelineDeps = {
  readonly sink: MessageSink
  readonly telemetry: Telemetry
  /**
   * Store content for an assistant message the fold hasn't seen yet — called
   * lazily on the first event per message so a history refetch landing after
   * connect still seeds (mirrors the hook's ref-read timing).
   */
  readonly seedFor: (messageId: string) => MessageSeed | undefined
  /**
   * First-failure tap, invoked at most once, at event time — the stream keeps
   * draining after an `error` event (a still-live backend stream isn't torn
   * down), so surfacing can't wait for the terminal result.
   */
  readonly onTurnError: (error: ChatTurnError) => void
  /**
   * Passive observer called for every decoded event before it folds. The
   * submit facade uses it for the genuinely-UI side effects the fold can't
   * own (temp→real chat-id migration, queue bubble lifecycle, typing
   * callbacks). A thrown observer error fails the turn.
   */
  readonly onEvent?: (event: ChatSSEEvent) => void
  /**
   * Stop draining on the first `error` event (submit parity: the old submit
   * loop threw out of its handler). Resume keeps draining — a still-live
   * backend stream isn't torn down by one error frame.
   */
  readonly haltOnErrorEvent?: boolean
  /**
   * Fires once per consumed id-stamped frame, as the checkpoint cursor
   * advances. A stream that later takes this chat over resumes from the last
   * published id instead of replaying the turn from its start.
   */
  readonly onLastEventId?: (lastEventId: number) => void
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

/** The stall monitor aborts the attempt with this reason; anything else is a client abort. */
const STALL_ABORT_REASON = 'stream-stalled'

function abortEndReason(signal: AbortSignal): StreamEndReason {
  return signal.reason === STALL_ABORT_REASON ? 'stalled' : 'aborted'
}

/**
 * Drain a connected turn stream. Resolves `completed` with the final
 * `TurnState`, `interrupted` on abort, or the first failure on the Err
 * channel. Decode failures are reported but don't fail the turn; transport
 * errors after the terminal `done` event are swallowed — the turn already
 * finished, only the connection close was left on the wire (HIG-5317).
 */
export function runTurnStream(
  deps: TurnPipelineDeps,
  ctx: TurnScope,
  response: Response,
  signal: AbortSignal,
  seed: TurnState = initialTurnState(ctx.chatId),
  checkpoint?: TurnCheckpoint,
): ResultAsync<ResumeCompletion, ChatTurnError> {
  const drain = async (): Promise<Result<ResumeCompletion, ChatTurnError>> => {
    let state = checkpoint?.state ?? seed
    let failure: ChatTurnError | null = null
    let streamCompleted = false
    const startedAt = Date.now()
    let frames = 0
    let endReason: StreamEndReason = 'closed-before-done'
    let endDetail: string | undefined
    let doneSucceeded: boolean | undefined
    const fail = (error: ChatTurnError) => {
      if (failure) return
      failure = error
      deps.onTurnError(error)
    }
    const advanceCursor = (persistedEventId: number | undefined) => {
      if (!checkpoint || persistedEventId === undefined) return
      if (checkpoint.lastEventId === persistedEventId) return
      checkpoint.lastEventId = persistedEventId
      deps.onLastEventId?.(persistedEventId)
    }

    try {
      for await (const frame of streamTurnFrames(response, signal)) {
        if (
          frame.persistedEventId !== undefined &&
          checkpoint?.lastEventId !== undefined &&
          frame.persistedEventId <= checkpoint.lastEventId
        ) {
          continue
        }
        frames += 1

        const decoded = decodeTurnEvent(ctx, frame.data)
        if (decoded.isErr()) {
          deps.telemetry.report(decoded.error, { turnId: ctx.turnId })
          advanceCursor(frame.persistedEventId)
          continue
        }
        const event = decoded.value
        if (event === null) {
          advanceCursor(frame.persistedEventId)
          continue
        }

        try {
          deps.onEvent?.(event)
        } catch (cause) {
          fail(toChatTurnError(cause, ctx))
          continue
        }

        // The observer may abort the turn (e.g. a cancelled queued send whose
        // held-open stream received `metadata` anyway) — settle before this
        // event folds so an orphaned stream never writes into the chat.
        if (signal.aborted) {
          endReason = abortEndReason(signal)
          return ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) })
        }

        if (event.type === 'metadata' && !isSubagentScoped(event)) {
          deps.telemetry.trace({
            kind: 'stream:metadata',
            ...ctx,
            conversationId: event.conversationId,
            lastUserMessageId: event.lastUserMessageId,
          })
        }

        if (event.type === 'error') {
          fail(backendTurnError(ctx, { kind: 'stream-error-event', message: event.error ?? '' }))
          endDetail = event.error ?? ''
          if (deps.haltOnErrorEvent) {
            endReason = 'error-event'
            break
          }
          advanceCursor(frame.persistedEventId)
          continue
        }
        if (event.type === 'queued') {
          advanceCursor(frame.persistedEventId)
          continue
        }
        if (event.type === 'done') {
          // Only the top-level `done` marks the turn complete; subagent-scoped
          // ones fire while the parent turn is still streaming.
          if (typeof event.parentToolCallId !== 'string') {
            streamCompleted = true
            endReason = 'done'
            doneSucceeded = event.succeeded !== false
            if (checkpoint) checkpoint.terminal = true
            if (event.succeeded === false) {
              fail(
                backendTurnError(ctx, {
                  kind: 'unsuccessful-completion',
                  message: INTERRUPTED_RESPONSE_MESSAGE,
                }),
              )
            }
          }
          advanceCursor(frame.persistedEventId)
          continue
        }

        const [next, patches] = applyEvent(ensureSeeded(state, event, deps.seedFor), event)
        deps.sink.applyAll(patches)
        state = next
        if (checkpoint) checkpoint.state = state
        advanceCursor(frame.persistedEventId)
      }
      // The generator can also end quietly on abort, without throwing.
      if (signal.aborted && !streamCompleted) endReason = abortEndReason(signal)
    } catch (cause) {
      if (signal.aborted || isAbortError(cause)) {
        endReason = abortEndReason(signal)
        return ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) })
      }
      endDetail = describeCause(cause)
      if (!streamCompleted) {
        endReason = 'transport-error'
        fail(sseConnectionError(ctx, cause))
      } else {
        // Transport read error after the terminal `done` — the turn (and any
        // tool action) already completed; surfacing it would show a false
        // "network error" banner.
        endReason = 'transport-error-after-done'
      }
    } finally {
      deps.telemetry.trace({
        kind: 'stream:ended',
        ...ctx,
        reason: endReason,
        frames,
        durationMs: Date.now() - startedAt,
        lastEventId: checkpoint?.lastEventId,
        succeeded: doneSucceeded,
        detail: endDetail,
      })
      // The turn settled one way or another — the facades' follow-up store
      // writes (placeholder cleanup, failure stamps) need every patch applied.
      deps.sink.flush()
    }

    return failure ? err(failure) : ok({ _tag: 'completed', state })
  }

  return new ResultAsync(drain())
}
